import React, { useState } from 'react';
import { Copy, Plus, RefreshCw } from 'lucide-react';
import { ReferralService } from '../../services/referralService';
import { ReferralCode, ReferralRedemption } from '../../types/referral';
import { format } from 'date-fns';


const ReferralManager: React.FC = () => {
  const [referrals, setReferrals] = useState<ReferralCode[]>([]);
  const [redemptions, setRedemptions] = useState<ReferralRedemption[]>([]);
  const [copied, setCopied] = useState<string | null>(null);
  const referralService = ReferralService.getInstance();
  const groupId = 'primary'; // Replace with actual group ID retrieval logic

  React.useEffect(() => {
    loadReferralData();
  }, [groupId]);

  const loadReferralData = () => {
    const groupReferrals = referralService.getReferralsByGroup(groupId);
    const groupRedemptions = referralService.getRedemptionsByGroup(groupId);
    setReferrals(groupReferrals);
    setRedemptions(groupRedemptions);
  };

  const handleGenerateCode = async () => {
    const newReferral = referralService.generateReferralCode(groupId);
    setReferrals([...referrals, newReferral]);
  };

  const handleCopyCode = (code: string) => {
    navigator.clipboard.writeText(code);
    setCopied(code);
    setTimeout(() => setCopied(null), 2000);
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h3 className="text-lg font-medium text-gray-900">Invitations Users</h3>
          <p className="mt-1 text-sm text-gray-500">
            Share your invitation code and get one month free when a new group signs up
          </p>
        </div>
        <button
          onClick={handleGenerateCode}
          className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-purple-600 hover:bg-purple-700"
        >
          <Plus className="h-4 w-4 mr-2" />
          Generate Code
        </button>
      </div>

      {/* Active Referral Codes */}
      <div className="bg-white shadow-sm rounded-lg overflow-hidden">
        <div className="px-4 py-5 sm:p-6">
          <h4 className="text-base font-medium text-gray-900 mb-4">
            Active Referral Codes
          </h4>
          <div className="space-y-4">
            {referrals.map((referral) => (
              <div
                key={referral.id}
                className="flex items-center justify-between p-4 bg-gray-50 rounded-lg"
              >
                <div>
                  <div className="text-lg font-medium text-gray-900">
                    {referral.code}
                  </div>
                  <div className="text-sm text-gray-500">
                    Used {referral.usageCount} times
                  </div>
                </div>
                <button
                  onClick={() => handleCopyCode(referral.code)}
                  className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                >
                  {copied === referral.code ? (
                    <RefreshCw className="h-4 w-4 mr-2 text-green-500" />
                  ) : (
                    <Copy className="h-4 w-4 mr-2" />
                  )}
                  {copied === referral.code ? 'Copied!' : 'Copy Code'}
                </button>
              </div>
            ))}
            {referrals.length === 0 && (
              <p className="text-center text-gray-500 py-4">
                No active referral codes. Generate one to get started!
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Redemption History */}
      <div className="bg-white shadow-sm rounded-lg overflow-hidden">
        <div className="px-4 py-5 sm:p-6">
          <h4 className="text-base font-medium text-gray-900 mb-4">
            Redemption History
          </h4>
          <div className="space-y-4">
            {redemptions.map((redemption) => (
              <div
                key={redemption.id}
                className="p-4 bg-gray-50 rounded-lg"
              >
                <div className="flex justify-between items-start">
                  <div>
                    <div className="text-sm font-medium text-gray-900">
                      {redemption.referrerGroupId === groupId
                        ? 'Your referral was used'
                        : 'You used a referral code'}
                    </div>
                    <div className="text-sm text-gray-500">
                      {format(redemption.redeemedAt, 'MMM d, yyyy')}
                    </div>
                  </div>
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                    100% off applied
                  </span>
                </div>
              </div>
            ))}
            {redemptions.length === 0 && (
              <p className="text-center text-gray-500 py-4">
                No referrals have been redeemed yet.
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReferralManager; 